/**
 * Card hold, step 2 — "Show the card check" (approved mockup
 * https://claude.ai/artifact/Hq93GUWq3pbxXLACELEYB9, moment 1).
 *
 * When a card is saved, Stripe's SetupIntent asks the bank to check it. No
 * money moves. Until now we never showed that answer. These pieces show it
 * right away, in the same words on every page that saves a card:
 *
 *   · CheckingCard     — "Checking your card with your bank…" while it runs
 *   · CardCheckedLine  — green: "Card checked: Visa ending 4242 · Your bank
 *                        says this card is good."
 *   · CardCheckFailed  — red: "Card didn't pass the check … Nothing was
 *                        charged." with Try another card
 *   · HoldNote         — the blue "We'll place a hold…" box. Only shown when
 *                        the server says so (card_hold.wording_enabled, ships
 *                        OFF, never for Net 30 or work-order customers).
 *
 * `cardCheckOutcome` turns Stripe.js's confirmCardSetup result into one of
 * four answers, so a half-typed card number never reads as "the bank said no".
 *
 * Nothing here charges, holds or retries a card.
 */
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── The answer ─────────────────────────────────────────────────────────────

/** The parts of Stripe.js's SetupIntentResult we read. */
export interface SetupResultLike {
    error?: {
        type?: string;
        code?: string;
        decline_code?: string;
        message?: string;
    } | null;
    setupIntent?: {
        status?: string;
        payment_method?: string | { id: string } | null;
    } | null;
}

export type CardCheckOutcome =
    /** The bank said yes. Save the card. */
    | { kind: "passed"; paymentMethodId: string }
    /** The bank said no (or a detail on the card was wrong). Red box. */
    | { kind: "failed"; reason: string }
    /** The form isn't filled in yet. Small inline note; nothing was checked. */
    | { kind: "fix"; message: string }
    /** We couldn't reach the bank. Small inline note; try again. */
    | { kind: "trouble"; message: string };

export type CardCheckAudience = "customer" | "office";

const CUSTOMER_REASONS: Record<string, string> = {
    incorrect_number: "The card number looks wrong.",
    invalid_number: "The card number looks wrong.",
    incorrect_cvc: "The security code on the back looks wrong.",
    invalid_cvc: "The security code on the back looks wrong.",
    incorrect_zip: "The ZIP code doesn't match this card.",
    expired_card: "This card is expired.",
    invalid_expiry_month: "The card's date looks wrong.",
    invalid_expiry_year: "The card's date looks wrong.",
    setup_intent_authentication_failure: "Your bank's approval step wasn't finished.",
};

const OFFICE_REASONS: Record<string, string> = {
    incorrect_number: "The card number is wrong.",
    invalid_number: "The card number is wrong.",
    incorrect_cvc: "The card's security code is wrong.",
    invalid_cvc: "The card's security code is wrong.",
    incorrect_zip: "The card's ZIP code is wrong.",
    expired_card: "The card is expired.",
    invalid_expiry_month: "The card's date is wrong.",
    invalid_expiry_year: "The card's date is wrong.",
    setup_intent_authentication_failure: "The bank's approval step wasn't finished.",
};

/** The bank's answer, in plain words for who is reading. */
export function cardCheckReason(code: string | null | undefined, audience: CardCheckAudience): string {
    const table = audience === "office" ? OFFICE_REASONS : CUSTOMER_REASONS;
    if (code && table[code]) return table[code]!;
    return audience === "office" ? "The bank said no to this card." : "Your bank said no to this card.";
}

export function cardCheckOutcome(result: SetupResultLike, audience: CardCheckAudience): CardCheckOutcome {
    const err = result.error;
    if (err) {
        // Stripe.js stops a half-typed card before it ever reaches the bank.
        if (err.type === "validation_error") {
            return { kind: "fix", message: err.message || "Finish typing the card, then try again." };
        }
        // A real "no" from the bank, or a detail on the card was wrong.
        if (err.type === "card_error" || err.code === "setup_intent_authentication_failure") {
            return { kind: "failed", reason: cardCheckReason(err.decline_code || err.code, audience) };
        }
        return {
            kind: "trouble",
            message:
                audience === "office"
                    ? "We couldn't reach the bank. Nothing was saved. Try again."
                    : "We couldn't reach your bank. Nothing was charged. Please try again.",
        };
    }
    const si = result.setupIntent;
    const pm = si?.payment_method;
    const paymentMethodId = typeof pm === "string" ? pm : pm?.id;
    if (si?.status === "succeeded" && paymentMethodId) return { kind: "passed", paymentMethodId };
    // Anything short of "succeeded" is not a pass.
    return { kind: "failed", reason: cardCheckReason(null, audience) };
}

// ─── The look ───────────────────────────────────────────────────────────────

/** "visa" → "Visa", "amex" → "Amex", "mastercard" → "Mastercard". */
export function brandName(brand: string | null | undefined): string {
    const b = (brand ?? "").trim().toLowerCase();
    if (!b || b === "unknown") return "Card";
    if (b === "mastercard") return "Mastercard";
    if (b === "american express" || b === "amex") return "Amex";
    return b.charAt(0).toUpperCase() + b.slice(1);
}

function BrandBadge({ brand }: { brand: string | null | undefined }) {
    const label = brandName(brand) === "Card" ? "CARD" : brandName(brand).toUpperCase().slice(0, 4);
    return (
        <span
            aria-hidden
            className="grid h-6 w-9 shrink-0 place-items-center rounded bg-[#1a1f71] text-[10px] font-extrabold italic text-white"
        >
            {label}
        </span>
    );
}

/** Customer pages are always light; the office dialog follows the app theme. */
type Variant = "public" | "office";

export function CheckingCard({ variant = "public" }: { variant?: Variant }) {
    return (
        <div
            role="status"
            data-testid="card-checking"
            className={cn(
                "grid gap-0.5 rounded-[10px] border-[1.5px] p-2.5",
                variant === "public" ? "border-[#dde3eb] bg-white text-[#14213a]" : "border-border bg-background",
            )}
        >
            <span className="flex items-center gap-2 text-[13.5px] font-bold">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#004AAD]" aria-hidden />
                Checking {variant === "office" ? "the card with the bank" : "your card with your bank"}…
            </span>
            <span className={cn("text-[12.5px]", variant === "public" ? "text-[#5d6b7e]" : "text-muted-foreground")}>
                This takes a few seconds. Nothing is charged.
            </span>
        </div>
    );
}

export function CardCheckedLine({
    brand,
    last4,
    variant = "public",
    trailing,
}: {
    brand: string | null | undefined;
    last4: string | null | undefined;
    variant?: Variant;
    /** Optional extra on the right (the office's ⓘ). */
    trailing?: React.ReactNode;
}) {
    const card = last4 ? `${brandName(brand)} ending ${last4}` : brandName(brand);
    return (
        <div
            data-testid="card-checked"
            className={cn(
                "flex items-center gap-2.5 rounded-[10px] border-[1.5px] p-2.5",
                variant === "public"
                    ? "border-[#86efac] bg-[#ecfdf5] text-[#14213a]"
                    : "border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100",
            )}
        >
            <BrandBadge brand={brand} />
            <span className="min-w-0 flex-1 text-[13px]">
                <b className="block">Card checked: {card}</b>
                {variant === "office" ? "The bank says this card is good." : "Your bank says this card is good."}
            </span>
            {trailing}
            <span
                aria-hidden
                className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#047857] text-[11px] font-extrabold text-white"
            >
                ✓
            </span>
        </div>
    );
}

export function CardCheckFailed({
    reason,
    nextStep,
    onTryAgain,
    variant = "public",
}: {
    reason: string;
    /** What to do instead, e.g. "…or send your request and we'll take a card when we call." */
    nextStep: string;
    onTryAgain: () => void;
    variant?: Variant;
}) {
    return (
        <div
            role="alert"
            data-testid="card-check-failed"
            className={cn(
                "grid gap-2 rounded-[10px] border-[1.5px] p-3",
                variant === "public"
                    ? "border-[#f5b5ae] bg-[#fff6f5] text-[#14213a]"
                    : "border-red-300 bg-red-50 text-red-950 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100",
            )}
        >
            <span
                className={cn(
                    "text-[11px] font-bold uppercase tracking-[0.07em]",
                    variant === "public" ? "text-[#b3261e]" : "text-red-700 dark:text-red-300",
                )}
            >
                Card didn&rsquo;t pass the check
            </span>
            <span className="text-[14px]" data-testid="card-check-failed-text">
                {reason} Nothing was charged. {nextStep}
            </span>
            <button
                type="button"
                onClick={onTryAgain}
                data-testid="card-check-try-another"
                className={cn(
                    "inline-flex h-10 items-center justify-center rounded-[9px] border-[1.5px] px-3 text-[14px] font-bold",
                    variant === "public"
                        ? "border-[#cbd3dd] bg-white text-[#14213a]"
                        : "border-border bg-background text-foreground hover:bg-secondary",
                )}
            >
                Try another card
            </button>
        </div>
    );
}

export function formatHoldAmount(cents: number): string {
    return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The first sentence of the blue box. */
export function holdSentence(amountCents: number | null | undefined): string {
    return typeof amountCents === "number" && amountCents > 0
        ? `We’ll place a hold for ${formatHoldAmount(amountCents)} the day before your visit.`
        : "We’ll place a hold for your visit’s price the day before.";
}

export function HoldNote({ amountCents }: { amountCents: number | null | undefined }) {
    return (
        <div
            data-testid="hold-note"
            className="rounded-[10px] border border-[#b9cdf0] bg-[#e8f0fb] px-3 py-2.5 text-[13.5px] font-semibold text-[#0f3470]"
        >
            {holdSentence(amountCents)} You&rsquo;re only charged after the work is done.
            <small className="mt-0.5 block text-[12.5px] font-medium text-[#33507f]">
                A hold is like a hotel hold. It&rsquo;s not a charge. If we cancel, it goes away.
            </small>
        </div>
    );
}
